import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';

function PublicWinnerPage() {
  const { cycleId } = useParams();
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadWinner = async () => {
      try {
        if (!cycleId) {
          setError('Invalid winner link');
          setLoading(false);
          return;
        }

        const winnerDoc = await getDoc(doc(db, 'cycleWinners', cycleId));
        
        if (winnerDoc.exists()) {
          setWinner({ id: winnerDoc.id, ...winnerDoc.data() });
        } else {
          setError('Winner not found');
        }
      } catch (err) {
        console.error('Error loading winner:', err);
        setError('Failed to load winner');
      } finally {
        setLoading(false);
      }
    };

    loadWinner();
  }, [cycleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center p-4">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-md text-center border border-white/20">
          <h1 className="text-3xl font-bold text-white mb-4">❌ {error}</h1>
          <a 
            href="/"
            className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-all"
          >
            Go to SubGames
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-2xl w-full border-4 border-yellow-400/50 shadow-2xl">
        {/* Trophy Header */}
        <div className="text-center mb-8">
          <div className="text-8xl mb-4">🏆</div>
          <h1 className="text-5xl font-bold text-yellow-300 mb-2">Cycle Winner!</h1>
          <p className="text-white/70 text-lg">SubGames Competition</p>
        </div>

        {/* Winner Info */}
        <div className="bg-white/20 backdrop-blur rounded-xl p-6 mb-6">
          {winner?.winnerPhotoURL && (
            <img 
              src={winner.winnerPhotoURL} 
              alt={winner.winnerName}
              className="w-32 h-32 rounded-full border-4 border-yellow-400 mx-auto mb-4"
            />
          )}
          
          <h2 className="text-4xl font-bold text-white text-center mb-4">
            {winner?.winnerName || 'Anonymous'}
          </h2>
          
          <div className="text-center space-y-2">
            <div className="text-white">
              <span className="font-bold text-3xl text-yellow-300">{winner?.finalScore || 0}</span>
              <span className="text-xl ml-2">points</span>
            </div>
            <div className="text-white/80">
              <span className="font-bold text-xl">{winner?.supporterCount || 0}</span>
              <span className="ml-2">supporters</span>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="space-y-4">
          {winner?.promotionalURL && (
            <a
              href={winner.promotionalURL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 px-6 rounded-xl transition-all shadow-lg text-center text-lg"
            >
              🔗 Check Out {winner.winnerName}'s Channel
            </a>
          )}
          
          <a
            href="/"
            className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg text-center text-lg"
          >
            🎮 Play SubGames & Support Creators
          </a>
        </div>

        {/* About Section */}
        <div className="mt-8 bg-white/10 rounded-xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-3 text-center">The SubGames</h3>
          <p className="text-white/80 text-center">
            Play mini-games to compete against other creators. Winners are determined by points earned each day.
          </p>
        </div>

        {/* Share Prompt */}
        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">
            Are you a creator? Join SubGames and compete for promotion
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicWinnerPage;
